import { ExternalLink, ShieldCheck, Globe2 } from "lucide-react";

/**
 * Industry memberships / affiliations strip.
 * Add more entries to `members` as new memberships are onboarded.
 */
const members = [
  {
    key: "freightnet",
    name: "Freightnet",
    tagline: "Global network of independent freight forwarders",
    description:
      "JMR Global Logistics is a Premium Member of Freightnet — a worldwide network of trusted, vetted freight forwarders connecting shippers and agents across every major trade lane.",
    url: "https://www.freightnet.com/profile/277923.htm",
    badgeImage: "https://www.freightnet.com/images/m_277923.jpg",
    badgeAlt: "Freightnet Premium Member — JMR Global Logistics",
    accent: "Premium Member",
  },
];

export default function Affiliations() {
  return (
    <section
      id="affiliations"
      data-testid="affiliations-section"
      className="bg-white border-t border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12 md:mb-16">
          <div className="md:col-span-7 reveal">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-sky-600 mb-4">
              Affiliations &amp; Memberships
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter text-slate-900 leading-[1.02]">
              Trusted through<br />
              <span className="text-slate-400">global networks.</span>
            </h2>
          </div>
          <div className="md:col-span-5 flex items-end reveal">
            <p className="text-slate-600 text-lg md:text-xl">
              We collaborate with vetted logistics networks worldwide — extending our reach, reliability, and cargo visibility across every continent we serve.
            </p>
          </div>
        </div>

        {/* Members */}
        <div className={`grid gap-6 ${members.length === 1 ? "md:grid-cols-1 max-w-3xl mx-auto" : "md:grid-cols-2 lg:grid-cols-3"}`}>
          {members.map((m) => (
            <a
              key={m.key}
              href={m.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`affiliation-${m.key}`}
              className="group relative block border border-slate-200 bg-white hover:bg-sky-50/40 transition-colors reveal overflow-hidden"
            >
              {/* Header band */}
              <div className="bg-blue-950 text-white px-6 md:px-8 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 bg-sky-400 text-blue-950">
                    <ShieldCheck className="w-5 h-5" strokeWidth={2} />
                  </span>
                  <span className="font-mono text-[11px] uppercase tracking-widest text-sky-300">
                    {m.accent}
                  </span>
                </div>
                <ExternalLink className="w-4 h-4 text-white/40 group-hover:text-sky-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
              </div>

              {/* Body */}
              <div className="p-6 md:p-10">
                {m.badgeImage && (
                  <div className="mb-6 flex justify-start">
                    <img
                      src={m.badgeImage}
                      alt={m.badgeAlt || `${m.name} member badge`}
                      width="250"
                      height="100"
                      loading="lazy"
                      decoding="async"
                      className="border border-slate-200 shadow-sm"
                    />
                  </div>
                )}
                <div className="flex items-baseline gap-3 mb-3">
                  <Globe2 className="w-6 h-6 text-sky-500 shrink-0 self-center" strokeWidth={1.5} />
                  <h3 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-slate-900 group-hover:text-sky-600 transition-colors">
                    {m.name}
                  </h3>
                </div>
                <p className="text-slate-500 text-sm md:text-base font-medium uppercase tracking-wide mb-4">
                  {m.tagline}
                </p>
                <p className="text-slate-700 text-base md:text-lg leading-relaxed">
                  {m.description}
                </p>
                <p className="mt-6 font-mono text-xs uppercase tracking-widest text-sky-600 inline-flex items-center gap-2">
                  {new URL(m.url).hostname.replace(/^www\./, "")}
                  <span className="w-1.5 h-1.5 bg-sky-500 animate-pulse" />
                </p>
              </div>

              {/* Bottom underline animation */}
              <div className="absolute bottom-0 left-0 h-0.5 w-0 bg-sky-400 group-hover:w-full transition-all duration-500" />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
